/**
 * ReportsPage — generic reports listing page shell.
 *
 * Encapsulates the pattern shared by all 4 section Reports components:
 *   - TopBar with "New Report" button
 *   - Search input
 *   - Optional folder sidebar
 *   - Loading skeletons / empty state / animated grid of ReportCards
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonCard } from '@/components/ui/Skeleton';
import { TopBar } from '@/components/layout/TopBar';
import { staggerContainer } from '@/design/animations';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { ReportCard } from './ReportCard';
import { FolderSidebar } from './FolderSidebar';
import type { Report, ReportListing } from '@/lib/reports.types';
import type { LucideIcon } from 'lucide-react';

interface ReportsPageProps {
  /** Section ID for API calls (e.g., 'product'). */
  sectionId: string;
  /** Route prefix (e.g., '/product'). */
  sectionRoute: string;
  /** Title shown in TopBar. */
  title: string;
  /** Subtitle shown in TopBar. */
  subtitle: string;
  /** Icon for empty state. */
  emptyIcon: LucideIcon;
  /** Description for empty state (no reports). */
  emptyDescription?: string;
  /** Whether to show folder sidebar (default: true when folders exist). */
  showFolders?: boolean;
  /** Accent color for folder sidebar. */
  accentColor?: 'brand' | 'sky' | 'emerald';
  /** Icon for report cards (optional). */
  cardIcon?: React.ReactNode;
  /** Background class for the card icon container. */
  cardIconBgClass?: string;
  /** Render section-specific config badges on each card. */
  renderBadges?: (config: Record<string, unknown>) => React.ReactNode;
}

export function ReportsPage({
  sectionId,
  sectionRoute,
  title,
  subtitle,
  emptyIcon: EmptyIcon,
  emptyDescription,
  showFolders = true,
  accentColor = 'brand',
  cardIcon,
  cardIconBgClass,
  renderBadges,
}: ReportsPageProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<ReportListing>({
    queryKey: ['reports', sectionId],
    queryFn: () => api.get<ReportListing>(endpoints.reports.list(sectionId)),
  });

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['reports', sectionId] });
  }, [queryClient, sectionId]);

  const folders = data?.folders || [];
  const allReports = (data?.reports || []).map((r) => r.report);

  const reports = allReports.filter((r) => {
    // Folder filtering (only when folders exist and showFolders is enabled)
    if (showFolders && folders.length > 0) {
      if (activeFolderId && r.folderId !== activeFolderId) return false;
      if (!activeFolderId && r.folderId) return false;
    }
    // Search filtering
    if (search) {
      const q = search.toLowerCase();
      return r.name.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q);
    }
    return true;
  });

  // Compute report counts for folder sidebar
  const reportCounts: Record<string, number> = {};
  reportCounts['_unfiled'] = allReports.filter((r) => !r.folderId).length;
  for (const folder of folders) {
    reportCounts[folder.id] = allReports.filter((r) => r.folderId === folder.id).length;
  }

  const hasFolders = showFolders && folders.length > 0;

  return (
    <>
      <TopBar
        title={title}
        subtitle={subtitle}
        actions={
          <Button icon={<Plus />} onClick={() => navigate(`${sectionRoute}/new`)}>
            New Report
          </Button>
        }
      />

      <div className="px-8 py-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 max-w-sm">
            <Input
              icon={<Search />}
              placeholder="Search reports..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className={hasFolders ? 'flex gap-6' : undefined}>
          {hasFolders && (
            <FolderSidebar
              folders={folders}
              activeFolderId={activeFolderId}
              onFolderChange={setActiveFolderId}
              reportCounts={reportCounts}
              accentColor={accentColor}
            />
          )}

          <div className="flex-1">
            {isLoading ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            ) : reports.length === 0 ? (
              <EmptyState
                icon={EmptyIcon}
                title={search ? 'No reports match your search' : 'No reports yet'}
                description={
                  search
                    ? 'Try adjusting your search terms'
                    : emptyDescription || `Create your first ${title.toLowerCase()} report to get started`
                }
                action={
                  !search && (
                    <Button icon={<Plus />} onClick={() => navigate(`${sectionRoute}/new`)}>
                      New Report
                    </Button>
                  )
                }
              />
            ) : (
              <motion.div
                className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4"
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
              >
                {reports.map((report) => (
                  <ReportCard
                    key={report.id}
                    report={report}
                    sectionId={sectionId}
                    sectionRoute={sectionRoute}
                    onRefresh={handleRefresh}
                    icon={cardIcon}
                    iconBgClass={cardIconBgClass}
                    renderBadges={renderBadges}
                  />
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
